/**
 * Lane C0-TRUEPX-GAME: whether the true-pixel clean-up (render/truepx/clean.ts) runs, readable on any thread. No imports:
 * the figure and ground workers load this without the settings.
 *
 * - Scene (scenery, buildings, ground): follows the `truePx` setting live (truePx.setting.ts); the ground workers get it
 *   with each ground they open (groundWorkers.ts).
 * - Figures: fixed for the page's life, as the pixel density is (density.ts): figure frames are cached in many places
 *   (worker replies, the frame store, the bake), so a change applies after a reload. The figure workers get it in their
 *   first message (`truePxMessage`).
 * On the main thread both start from the saved setting, read the first time either is asked for once the settings are
 * loaded (`registerTruePxSource`, from truePx.setting.ts).
 */
let scene = false;
let figures = false;
let started = false;
let source: (() => boolean | undefined) | null = null;

function ensure(): void {
  if (started || !source) return;
  const on = source();
  if (on === undefined) return;
  started = true;
  scene = figures = on;
}

/** Where the page's saved choice is read from (undefined until the settings are loaded). */
export function registerTruePxSource(f: () => boolean | undefined): void {
  source = f;
}

export function truePxScene(): boolean {
  ensure();
  return scene;
}
export function truePxFigures(): boolean {
  ensure();
  return figures;
}
/** The scene's flag from now on (the setting changed, or a ground worker was told). */
export function setTruePxScene(on: boolean): void {
  ensure();
  started = true;
  scene = on;
}

/** What the main thread tells a new figure worker. */
export const truePxMessage = (): { truePx: boolean } => ({ truePx: truePxFigures() });

/** Takes the figure flag from a `truePxMessage` (in a worker); true if the message was one. */
export function takeTruePx(data: unknown): boolean {
  if (!data || typeof data !== 'object' || !('truePx' in data) || 'id' in data) return false;
  started = true;
  figures = (data as { truePx: unknown }).truePx === true;
  return true;
}
